import { defineAsyncComponent } from 'vue';
import { mapActions, mapState } from 'pinia';
import { GlDrawer, GlModal, GlButton, GlFormRadioGroup, GlLoadingIcon, GlFormInput, GlForm } from '@gitlab/ui';
import { __, n__, sprintf } from '~/locale';
import { createAlert, VARIANT_WARNING } from '~/alert';
import PreviewItem from '~/batch_comments/components/preview_item.vue';
import { useBatchComments } from '~/batch_comments/store';
import { getContentWrapperHeight } from '~/lib/utils/dom_utils';
import { DRAWER_Z_INDEX } from '~/lib/utils/constants';
import { scrollToElement } from '~/lib/utils/scroll_utils';
import { useLegacyDiffs } from '~/diffs/stores/legacy_diffs';
import { fetchPolicies } from '~/lib/graphql';
import toast from '~/vue_shared/plugins/global_toast';
import MarkdownEditor from '~/vue_shared/components/markdown/markdown_editor.vue';
import MarkdownHeaderDivider from '~/vue_shared/components/markdown/header_divider.vue';
import { trackSavedUsingEditor } from '~/vue_shared/components/markdown/tracking';
import { CLEAR_AUTOSAVE_ENTRY_EVENT, CONTENT_EDITOR_PASTE } from '~/vue_shared/constants';
import markdownEditorEventHub from '~/vue_shared/components/markdown/eventhub';
import { updateText } from '~/lib/utils/text_markdown';
import { useNotes } from '~/notes/store/legacy_notes';
import diffsEventHub from '~/diffs/event_hub';
import { getIdFromGraphQLId } from '~/graphql_shared/utils';
import { STATUS_MERGED } from '~/issues/constants';
import { EVT_REVIEW_DRAWER_APPROVED } from '../../diffs/constants';
import reviewDrawerQuery from '../queries/review_drawer.query.graphql';
const REVIEW_STATES = {
    REVIEWED: 'reviewed',
    REQUESTED_CHANGES: 'requested_changes',
    APPROVED: 'approved'
};
const NOTIFY_EVENTS = {
    [REVIEW_STATES.APPROVED]: EVT_REVIEW_DRAWER_APPROVED
};
export default {
    name: 'ReviewDrawer',
    apollo: {
        mergeRequest: {
            fetchPolicy: fetchPolicies.NETWORK_ONLY,
            query: reviewDrawerQuery,
            variables () {
                return {
                    projectPath: this.projectPath,
                    iid: `${this.getNoteableData.iid}`
                };
            },
            update: (data)=>data.project?.mergeRequest ?? {}
        }
    },
    components: {
        GlDrawer,
        GlButton,
        GlFormRadioGroup,
        GlModal,
        GlLoadingIcon,
        GlFormInput,
        GlForm,
        PreviewItem,
        MarkdownEditor,
        MarkdownHeaderDivider,
        ApprovalPassword: defineAsyncComponent(()=>import('ee_component/batch_comments/components/approval_password.vue')),
        SummarizeMyReview: defineAsyncComponent(()=>import('ee_component/batch_comments/components/summarize_my_review.vue'))
    },
    inject: {
        canSummarize: {
            default: false
        }
    },
    emits: [
        'input',
        'draft-click'
    ],
    data () {
        return {
            isSubmitting: false,
            summarizeReviewLoading: false,
            showMarkdownEditor: false,
            mergeRequest: {},
            noteData: {
                noteable_type: '',
                noteable_id: '',
                note: '',
                approve: false,
                approval_password: '',
                reviewer_state: REVIEW_STATES.REVIEWED
            },
            discarding: false,
            showDiscardModal: false
        };
    },
    computed: {
        ...mapState(useLegacyDiffs, [
            'projectPath'
        ]),
        ...mapState(useBatchComments, [
            'sortedDrafts',
            'draftsCount',
            'drawerOpened'
        ]),
        ...mapState(useNotes, [
            'getNoteableData',
            'getNotesData',
            'getCurrentUserLastNote',
            'getUserData',
            'openState',
            'notesById'
        ]),
        getDrawerHeaderHeight () {
            if (!this.drawerOpened) return '0';
            return getContentWrapperHeight();
        },
        autocompleteDataSources () {
            return gl.GfmAutoComplete?.dataSources;
        },
        autosaveKey () {
            return `submit_review_dropdown/${this.getNoteableData.id}`;
        },
        userPermissions () {
            return this.mergeRequest.userPermissions ?? {};
        },
        reviewers () {
            return this.mergeRequest.reviewers?.nodes ?? [];
        },
        currentUserIsReviewer () {
            const currentUserId = this.getUserData?.id;
            if (!currentUserId) return false;
            return this.reviewers.some((reviewer)=>getIdFromGraphQLId(reviewer.id) === currentUserId);
        },
        currentUserIsAuthor () {
            const currentUserId = this.getUserData?.id;
            const authorId = this.mergeRequest.author?.id;
            if (!currentUserId || !authorId) return false;
            return getIdFromGraphQLId(authorId) === currentUserId;
        },
        canBeAddedAsReviewer () {
            // Mirrors the backend can_add_reviewer?; the numeric cap is enforced server-side.
            return this.mergeRequest.allowsMultipleReviewers || this.reviewers.length === 0;
        },
        showAssignReviewerHint () {
            // Avoid flashing the hint before the reviewers query resolves.
            if (this.$apollo.queries.mergeRequest.loading) return false;
            return this.userPermissions.updateMergeRequest && !this.currentUserIsAuthor && !this.currentUserIsReviewer && this.canBeAddedAsReviewer;
        },
        radioGroupOptions () {
            return [
                {
                    html: [
                        __('Comment'),
                        `<p class="help-text">
              ${__('Submit general feedback without explicit approval.')}
            </p>`
                    ].join('<br />'),
                    value: REVIEW_STATES.REVIEWED
                },
                {
                    html: [
                        __('Approve'),
                        `<p class="help-text">
              ${__('Submit feedback and approve these changes.')}
            </p>`
                    ].join('<br />'),
                    value: REVIEW_STATES.APPROVED,
                    disabled: !this.userPermissions.canApprove || this.openState === STATUS_MERGED
                },
                {
                    html: [
                        __('Request changes'),
                        `<p class="help-text">
              ${__('Submit feedback that should be addressed before merging.')}
            </p>`
                    ].join('<br />'),
                    value: REVIEW_STATES.REQUESTED_CHANGES
                }
            ];
        }
    },
    mounted () {
        this.noteData.noteable_type = this.noteableType;
        this.noteData.noteable_id = this.getNoteableData.id;
        this.noteData.merge_request_diff_head_sha = this.getNoteableData.diff_head_sha;
    },
    methods: {
        ...mapActions(useBatchComments, [
            'setDrawerOpened',
            'publishReview',
            'publishReviewInBatches',
            'discardDrafts',
            'clearDrafts'
        ]),
        async submitReview () {
            this.isSubmitting = true;
            if (this.userLastNoteWatcher) this.userLastNoteWatcher();
            if (this.$refs.markdownEditor) {
                trackSavedUsingEditor(this.$refs.markdownEditor.isContentEditorActive, 'MergeRequest_review');
            }
            try {
                const { note, reviewer_state: reviewerState } = this.noteData;
                const response = this.draftsCount > 0 ? await this.publishReviewInBatches(this.noteData) : await this.publishReview({
                    ...this.noteData
                });
                const summaryNoteId = response?.summary_note_id;
                const approvalRefused = Boolean(response?.reviewer_state_error);
                const unpublishedDraftsCount = response?.unpublished_drafts?.length || 0;
                const hasUnpublishedDrafts = unpublishedDraftsCount > 0;
                if (approvalRefused) {
                    createAlert({
                        message: sprintf(__('Your review was submitted, but the merge request was not approved: %{reason}'), {
                            reason: response.reviewer_state_error
                        }),
                        variant: VARIANT_WARNING
                    });
                }
                if (hasUnpublishedDrafts) {
                    createAlert({
                        message: sprintf(n__('Your review was submitted, but %{count} comment is still pending.', 'Your review was submitted, but %{count} comments are still pending.', unpublishedDraftsCount), {
                            count: unpublishedDraftsCount
                        }),
                        variant: VARIANT_WARNING
                    });
                }
                markdownEditorEventHub.$emit(CLEAR_AUTOSAVE_ENTRY_EVENT, this.autosaveKey);
                this.noteData.note = '';
                this.noteData.reviewer_state = REVIEW_STATES.REVIEWED;
                this.noteData.approval_password = '';
                if (note) {
                    this.userLastNoteWatcher = this.$watch(()=>summaryNoteId ? this.notesById[summaryNoteId] : this.getCurrentUserLastNote, (scrollNote)=>{
                        if (!scrollNote) return;
                        window.location.hash = `note_${scrollNote.id}`;
                        window.mrTabs?.tabShown('show');
                        setTimeout(()=>{
                            scrollToElement(document.getElementById(`note_${scrollNote.id}`));
                            if (!hasUnpublishedDrafts) {
                                this.clearDrafts();
                                this.setDrawerOpened(false);
                            }
                            this.isSubmitting = false;
                        });
                        this.userLastNoteWatcher();
                    }, {
                        deep: true
                    });
                } else {
                    if (reviewerState === REVIEW_STATES.APPROVED && !approvalRefused) {
                        window.mrTabs?.tabShown('show');
                    }
                    if (!hasUnpublishedDrafts) {
                        this.clearDrafts();
                        this.setDrawerOpened(false);
                    }
                    this.isSubmitting = false;
                }
                if (NOTIFY_EVENTS[reviewerState] && !approvalRefused) {
                    diffsEventHub.$emit(NOTIFY_EVENTS[reviewerState], {
                        summary: note ? 1 : 0,
                        comments: this.draftsCount
                    });
                }
            } catch (e) {
                const message = e?.data?.message || e?.response?.data?.message || __('Something went wrong. Please try again.');
                createAlert({
                    message,
                    captureError: true,
                    error: e
                });
                this.isSubmitting = false;
            }
        },
        async discardReviews () {
            this.discarding = true;
            try {
                await this.discardDrafts();
                this.setDrawerOpened(false);
                toast(__('Review discarded'));
            } finally{
                this.discarding = false;
            }
        },
        updateNote (note) {
            const textArea = this.$el.querySelector('textarea');
            if (textArea) {
                updateText({
                    textArea,
                    tag: note,
                    cursorOffset: 0,
                    wrap: false
                });
            } else {
                markdownEditorEventHub.$emit(CONTENT_EDITOR_PASTE, note);
            }
        }
    },
    DRAWER_Z_INDEX,
    modal: {
        cancelAction: {
            text: __('Keep review')
        },
        primaryAction: {
            text: __('Discard review'),
            attributes: {
                variant: 'danger'
            }
        }
    },
    formFieldProps: {
        id: 'review-note-body',
        name: 'review[note]',
        placeholder: __('Write a comment or drag your files here…'),
        'aria-label': __('Comment'),
        'data-testid': 'comment-textarea',
        class: 'gl-leading-[1.5]'
    },
    restrictedToolbarItems: [
        'full-screen'
    ],
    REVIEW_STATES
};
